import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { toast } from 'sonner';
import { getSupabaseClient } from '../../lib/supabase';
import { createClient } from '@supabase/supabase-js';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { 
  Package, 
  Plus, 
  Trash2, 
  FileText, 
  Printer, 
  AlertCircle, 
  Loader2, 
  CheckCircle2, 
  Percent, 
  IndianRupee,
  Receipt,
  Sparkles,
  Info
} from 'lucide-react';
import { ServiceBillPrintModal } from './ServiceBillPrintModal';

export interface ServiceBookingItemRecord {
  id: string;
  booking_id: string;
  item_name: string;
  quantity: number;
  unit_id: string;
  unit_price: number;
  gst_rate_percent: number;
  product_id?: string | null;
  created_at?: string;
}

export interface UnitItem {
  id: string;
  unit_name: string;
  unit_symbol?: string | null;
}

export interface InvoicePreviewLine {
  line_type: 'service' | 'material';
  item_name: string;
  quantity: number;
  unit_price: number;
  discount: number;
  taxable: number;
  gst_rate: number;
  cgst: number;
  sgst: number;
  igst: number;
  line_total: number;
  unit_id?: string | null;
}

export interface InvoicePreviewData {
  lines: InvoicePreviewLine[];
  subtotal: number;
  discount: number;
  taxable: number;
  cgst: number;
  sgst: number;
  igst: number;
  total: number;
  seller_state?: string;
  gst_enabled?: boolean;
}

export interface SalesInvoiceRecord {
  id: string;
  invoice_number: string;
  status: string;
  total_amount: number;
  taxable_value: number;
  cgst_amount: number;
  sgst_amount: number;
  discount_amount: number;
  created_at: string;
}

export interface SalesInvoiceItemRecord {
  id: string;
  item_name: string;
  quantity: number;
  unit_price: number;
  taxable_value: number;
  cgst_amount: number;
  sgst_amount: number;
  total_amount: number;
}

export interface ServiceBillingPanelProps {
  bookingId: string;
  bookingStatus: string;
  invoiceId?: string | null;
  role: 'professional' | 'staff';
  businessName?: string;
  onChanged?: () => void;
}

export function ServiceBillingPanel({
  bookingId,
  bookingStatus,
  invoiceId: initialInvoiceId,
  role,
  businessName,
  onChanged
}: ServiceBillingPanelProps) {
  const supabase = getSupabaseClient();

  // Service role admin fallback for querying rows if RLS policy on SELECT requires it
  const adminFallback = useMemo(() => {
    const url = import.meta.env.VITE_SUPABASE_URL;
    const sKey = import.meta.env.VITE_SUPABASE_SERVICE_ROLE_KEY;
    if (url && sKey) {
      return createClient(url, sKey);
    }
    return null;
  }, []);

  const [currentInvoiceId, setCurrentInvoiceId] = useState<string | null>(initialInvoiceId || null);
  const [showPrintModal, setShowPrintModal] = useState<boolean>(false);

  useEffect(() => {
    setCurrentInvoiceId(initialInvoiceId || null);
  }, [initialInvoiceId]);

  // Units list
  const [units, setUnits] = useState<UnitItem[]>([]);
  const [loadingUnits, setLoadingUnits] = useState(false);

  // Materials list
  const [materials, setMaterials] = useState<ServiceBookingItemRecord[]>([]);
  const [loadingMaterials, setLoadingMaterials] = useState(false);
  const [removingId, setRemovingId] = useState<string | null>(null);

  // Mini-form for adding material
  const [itemName, setItemName] = useState('');
  const [quantity, setQuantity] = useState('');
  const [unitId, setUnitId] = useState('');
  const [unitPrice, setUnitPrice] = useState('');
  const [gstRate, setGstRate] = useState('0');
  const [addingMaterial, setAddingMaterial] = useState(false);
  const [addError, setAddError] = useState<string | null>(null);

  // Discount controls (Staff only)
  const [discountType, setDiscountType] = useState<'none' | 'percent' | 'fixed'>('none');
  const [discountValue, setDiscountValue] = useState<string>('0');

  // Invoice Preview
  const [preview, setPreview] = useState<InvoicePreviewData | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);

  // Booking advance state
  const [bookingAdvance, setBookingAdvance] = useState<{
    advance_amount: number;
    advance_applied: number;
    advance_refunded: number;
    advance_mode?: string | null;
  }>({
    advance_amount: 0,
    advance_applied: 0,
    advance_refunded: 0
  });

  // Fetch booking advance details
  useEffect(() => {
    if (!supabase || !bookingId) return;
    supabase
      .from('service_bookings')
      .select('advance_amount, advance_applied, advance_refunded, advance_mode')
      .eq('id', bookingId)
      .maybeSingle()
      .then(({ data }: { data: any }) => {
        if (data) {
          setBookingAdvance({
            advance_amount: Number(data.advance_amount) || 0,
            advance_applied: Number(data.advance_applied) || 0,
            advance_refunded: Number(data.advance_refunded) || 0,
            advance_mode: data.advance_mode || null
          });
        }
      });
  }, [supabase, bookingId]);

  // Generating invoice
  const [generating, setGenerating] = useState(false);
  const [generateError, setGenerateError] = useState<string | null>(null);

  // Existing invoice record
  const [invoiceData, setInvoiceData] = useState<SalesInvoiceRecord | null>(null);
  const [invoiceItems, setInvoiceItems] = useState<SalesInvoiceItemRecord[]>([]);
  const [loadingInvoice, setLoadingInvoice] = useState(false);

  // Quick unit lookup map: unitId -> Unit Name / Symbol
  const unitMap = useMemo(() => {
    const map = new Map<string, string>();
    units.forEach(u => {
      map.set(u.id, u.unit_symbol || u.unit_name);
    });
    return map;
  }, [units]);

  // ----------------------------------------------------
  // 1. Fetch Units from public.units
  // ----------------------------------------------------
  const fetchUnits = useCallback(async () => {
    if (!supabase) return;
    setLoadingUnits(true);
    try {
      let { data, error } = await supabase
        .from('units')
        .select('id, unit_name, unit_symbol')
        .order('unit_name', { ascending: true });

      if ((!data || data.length === 0) && adminFallback) {
        const { data: fallbackData } = await adminFallback
          .from('units')
          .select('id, unit_name, unit_symbol')
          .order('unit_name', { ascending: true });
        if (fallbackData) data = fallbackData;
      }

      if (data) {
        setUnits(data);
      }
    } catch (err: any) {
      console.error('Error fetching units:', err);
    } finally {
      setLoadingUnits(false);
    }
  }, [supabase, adminFallback]);

  useEffect(() => {
    fetchUnits();
  }, [fetchUnits]);

  // ----------------------------------------------------
  // 2. Fetch Materials from service_booking_items
  // ----------------------------------------------------
  const fetchMaterials = useCallback(async () => {
    if (!supabase || !bookingId) return;
    setLoadingMaterials(true);
    try {
      let { data, error } = await supabase
        .from('service_booking_items')
        .select('*')
        .eq('booking_id', bookingId)
        .order('created_at', { ascending: true });

      // Fallback query if RLS restricted select
      if ((!data || data.length === 0) && adminFallback) {
        const { data: fallbackData } = await adminFallback
          .from('service_booking_items')
          .select('*')
          .eq('booking_id', bookingId)
          .order('created_at', { ascending: true });
        if (fallbackData) data = fallbackData;
      }

      setMaterials(data || []);
    } catch (err: any) {
      console.error('Error fetching materials:', err);
    } finally {
      setLoadingMaterials(false);
    }
  }, [supabase, bookingId, adminFallback]);

  useEffect(() => {
    fetchMaterials();
  }, [fetchMaterials]);

  // ----------------------------------------------------
  // 3. Fetch Existing Invoice (if currentInvoiceId is set)
  // ----------------------------------------------------
  const fetchInvoice = useCallback(async () => {
    if (!currentInvoiceId || !supabase) return;
    setLoadingInvoice(true);
    try {
      let { data: inv, error: invErr } = await supabase
        .from('sales_invoices')
        .select('id, invoice_number, status, total_amount, taxable_value, cgst_amount, sgst_amount, discount_amount, created_at')
        .eq('id', currentInvoiceId)
        .maybeSingle();

      if (!inv && adminFallback) {
        const { data: fallbackInv } = await adminFallback
          .from('sales_invoices')
          .select('id, invoice_number, status, total_amount, taxable_value, cgst_amount, sgst_amount, discount_amount, created_at')
          .eq('id', currentInvoiceId)
          .maybeSingle();
        inv = fallbackInv;
      }

      if (inv) {
        setInvoiceData(inv as SalesInvoiceRecord);

        // Fetch invoice items
        let { data: items } = await supabase
          .from('sales_invoice_items')
          .select('id, item_name, quantity, unit_price, taxable_value, cgst_amount, sgst_amount, total_amount')
          .eq('invoice_id', currentInvoiceId);

        if ((!items || items.length === 0) && adminFallback) {
          const { data: fallbackItems } = await adminFallback
            .from('sales_invoice_items')
            .select('id, item_name, quantity, unit_price, taxable_value, cgst_amount, sgst_amount, total_amount')
            .eq('invoice_id', currentInvoiceId);
          items = fallbackItems;
        }

        setInvoiceItems((items as SalesInvoiceItemRecord[]) || []);
      }
    } catch (err: any) {
      console.error('Error loading invoice:', err);
    } finally {
      setLoadingInvoice(false);
    }
  }, [currentInvoiceId, supabase, adminFallback]);

  useEffect(() => {
    if (currentInvoiceId) {
      fetchInvoice();
    } else {
      setInvoiceData(null);
      setInvoiceItems([]);
    }
  }, [currentInvoiceId, fetchInvoice]);

  // ----------------------------------------------------
  // 4. Fetch Invoice Preview
  // Only when status = 'completed' (or in_progress for estimate) and no invoice yet
  // ----------------------------------------------------
  const fetchPreview = useCallback(async () => {
    if (!supabase || !bookingId || currentInvoiceId) {
      setPreview(null);
      return;
    }

    // Preview only when status is 'completed'
    if (bookingStatus !== 'completed') {
      setPreview(null);
      setPreviewError(null);
      return;
    }

    setPreviewLoading(true);
    setPreviewError(null);

    try {
      // Role professional CANNOT send discount: must send null and 0
      const p_disc_type = role === 'staff' && discountType !== 'none' ? discountType : null;
      const p_disc_val = role === 'staff' && discountType !== 'none' ? Number(discountValue) || 0 : 0;

      const { data, error } = await supabase.rpc('preview_service_invoice', {
        p_booking_id: bookingId,
        p_discount_type: p_disc_type,
        p_discount_value: p_disc_val
      });

      if (error) {
        throw error;
      }

      setPreview(data as InvoicePreviewData);
    } catch (err: any) {
      console.error('Error in preview_service_invoice:', err);
      // Surface exact Postgres error message
      setPreviewError(err.message || 'Failed to calculate invoice preview');
    } finally {
      setPreviewLoading(false);
    }
  }, [supabase, bookingId, currentInvoiceId, bookingStatus, role, discountType, discountValue]);

  // Re-fetch preview when materials list, discount type, or discount value changes
  useEffect(() => {
    fetchPreview();
  }, [fetchPreview, materials]);

  // ----------------------------------------------------
  // 5. Add Material Handler
  // ----------------------------------------------------
  const handleAddMaterial = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!supabase || !bookingId) return;

    if (!itemName.trim()) {
      toast.error('Item name is required');
      return;
    }

    const qtyNum = parseFloat(quantity);
    if (isNaN(qtyNum) || qtyNum <= 0) {
      toast.error('Quantity must be greater than 0');
      return;
    }

    if (!unitId) {
      toast.error('Please select a unit (Required)');
      return;
    }

    const rateNum = parseFloat(unitPrice);
    if (isNaN(rateNum) || rateNum < 0) {
      toast.error('Unit price must be 0 or greater');
      return;
    }

    const gstNum = parseFloat(gstRate) || 0;

    setAddingMaterial(true);
    setAddError(null);

    try {
      const { data, error } = await supabase.rpc('add_service_booking_material', {
        p_booking_id: bookingId,
        p_item_name: itemName.trim(),
        p_quantity: qtyNum,
        p_unit_id: unitId,
        p_unit_price: rateNum,
        p_product_id: null,
        p_gst_rate: gstNum
      });

      if (error) {
        throw error;
      }

      toast.success(`Added ${itemName.trim()} (${qtyNum})`);
      // Reset form
      setItemName('');
      setQuantity('');
      setUnitPrice('');
      setGstRate('0');

      // Refresh materials list and inform parent
      await fetchMaterials();
      onChanged?.();
    } catch (err: any) {
      console.error('Error adding material:', err);
      setAddError(err.message || 'Failed to add material');
      toast.error(err.message || 'Failed to add material');
    } finally {
      setAddingMaterial(false);
    }
  };

  // ----------------------------------------------------
  // 6. Remove Material Handler
  // ----------------------------------------------------
  const handleRemoveMaterial = async (itemId: string, name: string) => {
    if (!supabase) return;
    setRemovingId(itemId);

    try {
      const { error } = await supabase.rpc('remove_service_booking_material', {
        p_item_id: itemId
      });

      if (error) {
        throw error;
      }

      toast.success(`Removed ${name}`);
      await fetchMaterials();
      onChanged?.();
    } catch (err: any) {
      console.error('Error removing material:', err);
      toast.error(err.message || 'Failed to remove material');
    } finally {
      setRemovingId(null);
    }
  };

  // ----------------------------------------------------
  // 7. Generate Invoice Handler
  // ----------------------------------------------------
  const handleGenerateInvoice = async () => {
    if (!supabase || !bookingId) return;

    setGenerating(true);
    setGenerateError(null);

    try {
      const p_disc_type = role === 'staff' && discountType !== 'none' ? discountType : null;
      const p_disc_val = role === 'staff' && discountType !== 'none' ? Number(discountValue) || 0 : 0;

      const { data: invId, error } = await supabase.rpc('generate_service_invoice', {
        p_booking_id: bookingId,
        p_discount_type: p_disc_type,
        p_discount_value: p_disc_val
      });

      if (error) {
        throw error;
      }

      toast.success('GST Tax Invoice generated successfully!');
      setCurrentInvoiceId(invId);
      onChanged?.();
    } catch (err: any) {
      console.error('Error generating invoice:', err);
      // Surface exact backend error text
      setGenerateError(err.message || 'Failed to generate invoice');
      toast.error(err.message || 'Failed to generate invoice');
    } finally {
      setGenerating(false);
    }
  };

  // ----------------------------------------------------
  // 8. Print Invoice Handler
  // ----------------------------------------------------
  const handlePrint = () => {
    setShowPrintModal(true);
  };

  // Condition checks
  const canEditMaterials = !currentInvoiceId && ['in_progress', 'completed'].includes(bookingStatus);
  const isBranchStateMissing = (previewError && previewError.toLowerCase().includes('state')) ||
                               (generateError && generateError.toLowerCase().includes('state'));

  return (
    <div className="space-y-4">
      {/* Inline style for clean print layout */}
      <style>{`
        @media print {
          body * {
            visibility: hidden !important;
          }
          #service-invoice-printable, #service-invoice-printable * {
            visibility: visible !important;
          }
          #service-invoice-printable {
            position: fixed !important;
            left: 0 !important;
            top: 0 !important;
            width: 100% !important;
            margin: 0 !important;
            padding: 24px !important;
            background: white !important;
            color: black !important;
            border: 1px solid #ddd !important;
            box-shadow: none !important;
            z-index: 999999 !important;
          }
          .no-print {
            display: none !important;
          }
        }
      `}</style>

      {/* ======================================================== */}
      {/* SECTION 1: MATERIALS USED                                */}
      {/* ======================================================== */}
      <div className="p-3.5 rounded-xl bg-slate-800/90 border border-slate-700/80 text-slate-100 space-y-3">
        <div className="flex items-center justify-between">
          <span className="text-xs font-bold uppercase tracking-wider text-slate-300 flex items-center gap-1.5">
            <Package size={14} className="text-primary" />
            <span>Materials Used ({materials.length})</span>
          </span>
          {currentInvoiceId && (
            <span className="text-[10px] font-semibold text-emerald-400 bg-emerald-500/10 px-2 py-0.5 rounded border border-emerald-500/20">
              Invoiced (Locked)
            </span>
          )}
        </div>

        {/* Materials Table / List */}
        {loadingMaterials ? (
          <div className="p-4 text-center text-slate-400 text-xs flex items-center justify-center gap-2">
            <Loader2 size={14} className="animate-spin text-primary" />
            <span>Loading materials...</span>
          </div>
        ) : materials.length === 0 ? (
          <p className="text-xs text-slate-400 italic py-1">
            No additional materials recorded for this job.
          </p>
        ) : (
          <div className="overflow-x-auto rounded-lg border border-slate-700/60">
            <table className="w-full text-xs text-left">
              <thead className="bg-slate-900/80 text-[10px] text-slate-400 uppercase tracking-wider border-b border-slate-700/60">
                <tr>
                  <th className="p-2">Item Name</th>
                  <th className="p-2 text-center">Qty / Unit</th>
                  <th className="p-2 text-right">Rate</th>
                  <th className="p-2 text-center">GST %</th>
                  <th className="p-2 text-right">Amount</th>
                  {canEditMaterials && <th className="p-2 text-center w-8"></th>}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-700/40">
                {materials.map(m => {
                  const lineTotal = m.quantity * m.unit_price;
                  const unitLabel = unitMap.get(m.unit_id) || 'Unit';
                  return (
                    <tr key={m.id} className="hover:bg-slate-700/30 transition-colors">
                      <td className="p-2 font-medium text-white">{m.item_name}</td>
                      <td className="p-2 text-center text-slate-300">
                        {m.quantity} <span className="text-[11px] text-slate-400">{unitLabel}</span>
                      </td>
                      <td className="p-2 text-right text-slate-300 font-mono">
                        ₹{Number(m.unit_price).toFixed(2)}
                      </td>
                      <td className="p-2 text-center text-slate-400">
                        {m.gst_rate_percent}%
                      </td>
                      <td className="p-2 text-right font-bold text-white font-mono">
                        ₹{lineTotal.toFixed(2)}
                      </td>
                      {canEditMaterials && (
                        <td className="p-2 text-center">
                          <button
                            type="button"
                            onClick={() => handleRemoveMaterial(m.id, m.item_name)}
                            disabled={removingId === m.id}
                            className="text-rose-400 hover:text-rose-300 p-1 rounded hover:bg-rose-950/40 transition-colors"
                            title="Remove item"
                          >
                            {removingId === m.id ? (
                              <Loader2 size={13} className="animate-spin" />
                            ) : (
                              <Trash2 size={13} />
                            )}
                          </button>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Mini Add Material Form (Only if canEditMaterials) */}
        {canEditMaterials && (
          <form onSubmit={handleAddMaterial} className="pt-2 border-t border-slate-700/60 space-y-2.5">
            <span className="text-[11px] font-semibold text-slate-300 block">
              + Add Material / Part Used
            </span>

            {addError && (
              <div className="p-2 rounded-lg bg-rose-500/10 border border-rose-500/30 text-rose-300 text-[11px]">
                {addError}
              </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
              <div>
                <label className="block text-[10px] text-slate-400 mb-0.5">Item Name *</label>
                <Input
                  type="text"
                  placeholder="e.g. Copper Pipe, Motor Oil"
                  value={itemName}
                  onChange={e => setItemName(e.target.value)}
                  required
                  className="h-8 bg-slate-900 border-slate-700 text-white text-xs rounded-lg"
                />
              </div>

              <div className="grid grid-cols-2 gap-1.5">
                <div>
                  <label className="block text-[10px] text-slate-400 mb-0.5">Qty (step 0.001) *</label>
                  <Input
                    type="number"
                    step="0.001"
                    min="0.001"
                    placeholder="1.0"
                    value={quantity}
                    onChange={e => setQuantity(e.target.value)}
                    required
                    className="h-8 bg-slate-900 border-slate-700 text-white text-xs rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-[10px] text-slate-400 mb-0.5">Unit *</label>
                  <select
                    value={unitId}
                    onChange={e => setUnitId(e.target.value)}
                    required
                    className="w-full h-8 px-2 bg-slate-900 border border-slate-700 text-white text-xs rounded-lg focus:outline-none focus:ring-1 focus:ring-primary"
                  >
                    <option value="">Select Unit...</option>
                    {units.map(u => (
                      <option key={u.id} value={u.id}>
                        {u.unit_name} {u.unit_symbol ? `(${u.unit_symbol})` : ''}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-[10px] text-slate-400 mb-0.5">Unit Price (₹) *</label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="Rate per unit"
                  value={unitPrice}
                  onChange={e => setUnitPrice(e.target.value)}
                  required
                  className="h-8 bg-slate-900 border-slate-700 text-white text-xs rounded-lg"
                />
              </div>

              <div>
                <label className="block text-[10px] text-slate-400 mb-0.5">GST Rate (%)</label>
                <select
                  value={gstRate}
                  onChange={e => setGstRate(e.target.value)}
                  className="w-full h-8 px-2 bg-slate-900 border border-slate-700 text-white text-xs rounded-lg focus:outline-none focus:ring-1 focus:ring-primary"
                >
                  <option value="0">0% (Nil / Exempt)</option>
                  <option value="5">5% GST</option>
                  <option value="12">12% GST</option>
                  <option value="18">18% GST (Standard)</option>
                  <option value="28">28% GST</option>
                </select>
              </div>
            </div>

            <Button
              type="submit"
              size="sm"
              disabled={addingMaterial || loadingUnits}
              className="w-full sm:w-auto bg-primary hover:bg-primary/90 text-white text-xs h-8 px-3 rounded-lg font-semibold flex items-center justify-center gap-1.5"
            >
              {addingMaterial ? (
                <Loader2 size={13} className="animate-spin" />
              ) : (
                <Plus size={13} />
              )}
              <span>Add Material</span>
            </Button>
          </form>
        )}
      </div>

      {/* ======================================================== */}
      {/* SECTION 2: INVOICE PREVIEW & GENERATE                    */}
      {/* (Only when status === 'completed' and no invoice yet)    */}
      {/* ======================================================== */}
      {bookingStatus === 'completed' && !currentInvoiceId && (
        <div className="p-3.5 rounded-xl bg-slate-800/90 border border-slate-700/80 text-slate-100 space-y-3.5">
          <div className="flex items-center justify-between">
            <span className="text-xs font-bold uppercase tracking-wider text-slate-300 flex items-center gap-1.5">
              <FileText size={14} className="text-amber-400" />
              <span>GST Invoice Preview</span>
            </span>
            <span className="text-[10px] font-semibold text-amber-300 bg-amber-500/10 px-2 py-0.5 rounded border border-amber-500/20">
              Ready for Invoicing
            </span>
          </div>

          {/* Discount Control (Staff only) */}
          {role === 'staff' && (
            <div className="p-3 bg-slate-900/80 rounded-xl border border-slate-700/60 space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-[11px] font-semibold text-slate-300 flex items-center gap-1">
                  <Percent size={12} className="text-primary" />
                  <span>Business Discount (Staff Control)</span>
                </span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
                <div>
                  <select
                    value={discountType}
                    onChange={e => {
                      const t = e.target.value as 'none' | 'percent' | 'fixed';
                      setDiscountType(t);
                      if (t === 'none') setDiscountValue('0');
                    }}
                    className="w-full h-8 px-2 bg-slate-800 border border-slate-700 text-white text-xs rounded-lg focus:outline-none focus:ring-1 focus:ring-primary"
                  >
                    <option value="none">No Discount</option>
                    <option value="percent">Percentage (%)</option>
                    <option value="fixed">Fixed Amount (₹)</option>
                  </select>
                </div>
                {discountType !== 'none' && (
                  <div>
                    <Input
                      type="number"
                      step={discountType === 'percent' ? '1' : '0.01'}
                      min="0"
                      max={discountType === 'percent' ? '100' : undefined}
                      placeholder={discountType === 'percent' ? 'e.g. 10%' : 'e.g. 100'}
                      value={discountValue}
                      onChange={e => setDiscountValue(e.target.value)}
                      className="h-8 bg-slate-800 border-slate-700 text-white text-xs rounded-lg"
                    />
                  </div>
                )}
              </div>
            </div>
          )}

          {/* State / Backend Error Warning */}
          {previewError && (
            <div className="p-3 rounded-xl bg-rose-500/10 border border-rose-500/30 text-rose-300 text-xs space-y-1.5">
              <div className="flex items-start gap-2">
                <AlertCircle size={15} className="shrink-0 mt-0.5 text-rose-400" />
                <span className="font-semibold">{previewError}</span>
              </div>
              {isBranchStateMissing && (
                <div className="text-[11px] text-amber-300/90 pl-6 border-t border-rose-500/20 pt-1">
                  <strong>Notice:</strong> To generate GST invoices, please set the Branch State in business Settings &rarr; Branches.
                </div>
              )}
            </div>
          )}

          {/* Lines Table & Totals from preview */}
          {previewLoading ? (
            <div className="p-6 text-center text-slate-400 text-xs">
              <Loader2 size={18} className="animate-spin text-primary mx-auto mb-1.5" />
              <span>Calculating taxes & line items...</span>
            </div>
          ) : preview ? (
            <div className="space-y-3">
              {/* Lines table */}
              <div className="overflow-x-auto rounded-lg border border-slate-700/60">
                <table className="w-full text-xs text-left">
                  <thead className="bg-slate-900/90 text-[10px] text-slate-400 uppercase tracking-wider border-b border-slate-700/60">
                    <tr>
                      <th className="p-2">Item</th>
                      <th className="p-2 text-center">Type</th>
                      <th className="p-2 text-center">Qty</th>
                      <th className="p-2 text-right">Taxable</th>
                      <th className="p-2 text-center">GST</th>
                      <th className="p-2 text-right">Total</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-700/40">
                    {preview.lines.map((l, idx) => (
                      <tr key={idx} className="hover:bg-slate-700/20">
                        <td className="p-2 font-medium text-white">{l.item_name}</td>
                        <td className="p-2 text-center">
                          <span className={`px-1.5 py-0.2 rounded text-[9px] uppercase font-bold ${
                            l.line_type === 'service' 
                              ? 'bg-blue-500/20 text-blue-300 border border-blue-500/30'
                              : 'bg-purple-500/20 text-purple-300 border border-purple-500/30'
                          }`}>
                            {l.line_type}
                          </span>
                        </td>
                        <td className="p-2 text-center text-slate-300">{l.quantity}</td>
                        <td className="p-2 text-right font-mono text-slate-300">
                          ₹{Number(l.taxable).toFixed(2)}
                        </td>
                        <td className="p-2 text-center text-slate-400 text-[11px]">
                          {l.gst_rate}%
                        </td>
                        <td className="p-2 text-right font-mono font-bold text-white">
                          ₹{Number(l.line_total).toFixed(2)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Summary Totals Box */}
              <div className="p-3 bg-slate-900/90 rounded-xl border border-slate-700/60 space-y-1.5 text-xs">
                <div className="flex justify-between text-slate-300">
                  <span>Subtotal:</span>
                  <span className="font-mono">₹{Number(preview.subtotal).toFixed(2)}</span>
                </div>

                {Number(preview.discount) > 0 && (
                  <div className="flex justify-between text-emerald-400">
                    <span>Discount:</span>
                    <span className="font-mono">-₹{Number(preview.discount).toFixed(2)}</span>
                  </div>
                )}

                <div className="flex justify-between text-slate-300">
                  <span>Taxable Value:</span>
                  <span className="font-mono font-medium">₹{Number(preview.taxable).toFixed(2)}</span>
                </div>

                {Number(preview.cgst) > 0 && (
                  <div className="flex justify-between text-slate-400 text-[11px]">
                    <span>CGST:</span>
                    <span className="font-mono">₹{Number(preview.cgst).toFixed(2)}</span>
                  </div>
                )}

                {Number(preview.sgst) > 0 && (
                  <div className="flex justify-between text-slate-400 text-[11px]">
                    <span>SGST:</span>
                    <span className="font-mono">₹{Number(preview.sgst).toFixed(2)}</span>
                  </div>
                )}

                {Number(preview.igst) > 0 && (
                  <div className="flex justify-between text-slate-400 text-[11px]">
                    <span>IGST:</span>
                    <span className="font-mono">₹{Number(preview.igst).toFixed(2)}</span>
                  </div>
                )}

                <div className="flex justify-between text-sm font-bold text-white pt-2 border-t border-slate-700/80">
                  <span>Invoice Total:</span>
                  <span className="text-emerald-400 font-mono">₹{Number(preview.total).toFixed(2)}</span>
                </div>

                {/* Advance Adjustment & Collect Now */}
                {(() => {
                  const advRem = Math.max(0, (bookingAdvance.advance_amount || 0) - (bookingAdvance.advance_applied || 0) - (bookingAdvance.advance_refunded || 0));
                  const advAdjust = Math.min(advRem, Number(preview.total));
                  const collectNow = Math.max(0, Number(preview.total) - advAdjust);

                  return (
                    <div className="pt-2 border-t border-dashed border-slate-700/80 space-y-1">
                      <div className="flex justify-between text-amber-300">
                        <span>Advance adjust:</span>
                        <span className="font-mono font-bold">-₹{advAdjust.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between text-xs font-bold text-white bg-slate-800/80 p-2 rounded-lg border border-slate-700">
                        <span className="text-emerald-400">Collect now:</span>
                        <span className="text-emerald-400 font-mono text-sm">₹{collectNow.toFixed(2)}</span>
                      </div>
                    </div>
                  );
                })()}
              </div>

              {/* Generate Invoice Error */}
              {generateError && (
                <div className="p-2.5 rounded-lg bg-rose-500/10 border border-rose-500/30 text-rose-300 text-xs flex items-start gap-1.5">
                  <AlertCircle size={14} className="shrink-0 mt-0.5" />
                  <span>{generateError}</span>
                </div>
              )}

              {/* Generate Invoice Button */}
              <Button
                type="button"
                onClick={handleGenerateInvoice}
                disabled={generating || previewLoading}
                className="w-full bg-emerald-600 hover:bg-emerald-700 text-white text-xs h-10 font-bold rounded-xl shadow-md flex items-center justify-center gap-1.5"
              >
                {generating ? (
                  <>
                    <Loader2 size={14} className="animate-spin" />
                    <span>Generating Invoice...</span>
                  </>
                ) : (
                  <>
                    <Receipt size={14} />
                    <span>Generate GST Tax Invoice (₹{Number(preview.total).toFixed(2)})</span>
                  </>
                )}
              </Button>
            </div>
          ) : null}
        </div>
      )}

      {/* ======================================================== */}
      {/* SECTION 3: INVOICED READ-ONLY CARD                       */}
      {/* (When invoiceId is set)                                  */}
      {/* ======================================================== */}
      {currentInvoiceId && (
        <div id="service-invoice-printable" className="p-4 rounded-2xl bg-slate-800/95 border border-slate-700 shadow-md text-slate-100 space-y-4">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 pb-3 border-b border-slate-700/80">
            <div>
              {businessName && (
                <p className="text-[11px] font-bold tracking-wider text-slate-400 uppercase">
                  {businessName}
                </p>
              )}
              <div className="flex items-center gap-2 mt-0.5">
                <h3 className="text-sm font-bold text-white flex items-center gap-1.5">
                  <Receipt size={15} className="text-primary" />
                  <span>GST Tax Invoice</span>
                </h3>
                {invoiceData?.status && (
                  <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase ${
                    invoiceData.status === 'paid'
                      ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'
                      : 'bg-blue-500/20 text-blue-300 border border-blue-500/30'
                  }`}>
                    {invoiceData.status === 'paid' ? 'Paid' : 'Issued'}
                  </span>
                )}
              </div>
              <p className="font-mono text-xs text-primary font-bold mt-0.5">
                {invoiceData?.invoice_number || 'Loading invoice...'}
              </p>
            </div>

            <div className="flex items-center gap-2 no-print">
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={handlePrint}
                className="h-8 border-slate-600 text-slate-200 hover:bg-slate-700 text-xs rounded-lg"
              >
                <Printer size={13} className="mr-1" />
                <span>Print / Download</span>
              </Button>
            </div>
          </div>

          {loadingInvoice ? (
            <div className="p-4 text-center text-slate-400 text-xs flex items-center justify-center gap-2">
              <Loader2 size={14} className="animate-spin text-primary" />
              <span>Loading invoice details...</span>
            </div>
          ) : (
            <div className="space-y-3 text-xs">
              {/* Itemized Lines if loaded */}
              {invoiceItems.length > 0 && (
                <div className="overflow-x-auto rounded-lg border border-slate-700/60">
                  <table className="w-full text-xs text-left">
                    <thead className="bg-slate-900/90 text-[10px] text-slate-400 uppercase tracking-wider border-b border-slate-700/60">
                      <tr>
                        <th className="p-2">Item</th>
                        <th className="p-2 text-center">Qty</th>
                        <th className="p-2 text-right">Taxable</th>
                        <th className="p-2 text-right">GST</th>
                        <th className="p-2 text-right">Total</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-700/40">
                      {invoiceItems.map(item => (
                        <tr key={item.id} className="hover:bg-slate-700/20">
                          <td className="p-2 font-medium text-white">{item.item_name}</td>
                          <td className="p-2 text-center text-slate-300">{item.quantity}</td>
                          <td className="p-2 text-right font-mono text-slate-300">
                            ₹{Number(item.taxable_value).toFixed(2)}
                          </td>
                          <td className="p-2 text-right font-mono text-slate-400">
                            ₹{(Number(item.cgst_amount || 0) + Number(item.sgst_amount || 0)).toFixed(2)}
                          </td>
                          <td className="p-2 text-right font-mono font-bold text-white">
                            ₹{Number(item.total_amount).toFixed(2)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Tax & Total Summary Card */}
              <div className="p-3 bg-slate-900/90 rounded-xl border border-slate-700/60 space-y-1.5 text-xs">
                <div className="flex justify-between text-slate-300">
                  <span>Taxable Value:</span>
                  <span className="font-mono">₹{Number(invoiceData?.taxable_value || 0).toFixed(2)}</span>
                </div>

                {Number(invoiceData?.discount_amount || 0) > 0 && (
                  <div className="flex justify-between text-emerald-400">
                    <span>Discount:</span>
                    <span className="font-mono">-₹{Number(invoiceData?.discount_amount).toFixed(2)}</span>
                  </div>
                )}

                {Number(invoiceData?.cgst_amount || 0) > 0 && (
                  <div className="flex justify-between text-slate-400 text-[11px]">
                    <span>CGST:</span>
                    <span className="font-mono">₹{Number(invoiceData?.cgst_amount).toFixed(2)}</span>
                  </div>
                )}

                {Number(invoiceData?.sgst_amount || 0) > 0 && (
                  <div className="flex justify-between text-slate-400 text-[11px]">
                    <span>SGST:</span>
                    <span className="font-mono">₹{Number(invoiceData?.sgst_amount).toFixed(2)}</span>
                  </div>
                )}

                <div className="flex justify-between text-sm font-bold text-white pt-2 border-t border-slate-700/80">
                  <span>Total Amount:</span>
                  <span className="text-emerald-400 font-mono">
                    ₹{Number(invoiceData?.total_amount || 0).toFixed(2)}
                  </span>
                </div>

                {/* Advance Adjustment & Collect Now */}
                {(() => {
                  const invTotal = Number(invoiceData?.total_amount || 0);
                  const advApplied = (bookingAdvance.advance_applied || 0) > 0 
                    ? bookingAdvance.advance_applied 
                    : Math.min(bookingAdvance.advance_amount || 0, invTotal);
                  const collectNow = Math.max(0, invTotal - advApplied);

                  return advApplied > 0 ? (
                    <div className="pt-2 border-t border-dashed border-slate-700/80 space-y-1">
                      <div className="flex justify-between text-amber-300">
                        <span>Advance adjust:</span>
                        <span className="font-mono font-bold">-₹{advApplied.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between text-xs font-bold text-white bg-slate-800/80 p-2 rounded-lg border border-slate-700">
                        <span className="text-emerald-400">Collect now:</span>
                        <span className="text-emerald-400 font-mono text-sm">₹{collectNow.toFixed(2)}</span>
                      </div>
                    </div>
                  ) : null;
                })()}
              </div>

              <div className="p-2.5 rounded-lg bg-emerald-500/10 border border-emerald-500/20 text-emerald-300 text-[11px] flex items-center gap-2">
                <CheckCircle2 size={14} className="shrink-0 text-emerald-400" />
                <span>Job final price locked to invoice total: <strong>₹{Number(invoiceData?.total_amount || 0).toFixed(2)}</strong></span>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Blueprint Live Bill Print & Preview Modal */}
      <ServiceBillPrintModal
        bookingId={bookingId}
        isOpen={showPrintModal}
        onClose={() => setShowPrintModal(false)}
      />
    </div>
  );
}
export default ServiceBillingPanel;
